// 时光画册 (Time Album) — daily photo + caption + location, with calendar/map/story views.
import React from 'react';
import { SLOTS, emptyDay, parseDate, dateKey } from '../data/slots.js';
import { CAT_BY_ID } from '../data/categories.js';
import { INSIGHTS, INSIGHTS_BY_DATE } from '../data/insights.js';
import { TagSelect } from './panels.jsx';

// Compress a File/Blob image to JPEG dataURL, capped at maxDim and target quality.
function compressImage(file, maxDim = 1200, quality = 0.72) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const r = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * r);
          height = Math.round(height * r);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        try {
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (e) { reject(e); }
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function MONTH_KEY(y, m) { return `${y}-${String(m).padStart(2, '0')}`; }
function pad2(n) { return String(n).padStart(2, '0'); }

// 常用地点兜底预设（你录过的会自动并进来、排在前面）
const SEED_LOCATIONS = ['金山', '娘家', '婆家', '公司', '家'];
// 指定地点的药丸色（走主题 CSS 变量，不写死 hex）。未列出的用米咖兜底
const LOC_COLORS = {
  '金山': 'var(--c-soc3)',   // 土豪金
  '娘家': 'var(--c-exer2)',  // 浅粉
  '婆家': 'var(--c-exer)',   // 玫红
  '公司': 'var(--c-prep)',   // 深蓝
};
const locColor = (name) => LOC_COLORS[name] || 'var(--c-soc1)';

// GPS 命名地点绑定：{name, lat, lng}[]，存独立 localStorage（注：暂未进 JSON 主备份）
const LOC_BIND_KEY = 'album-loc-bindings';
const LOC_BIND_RADIUS_M = 200; // 进入半径内即认作同一地点
function loadBindings() { try { return JSON.parse(localStorage.getItem(LOC_BIND_KEY)) || []; } catch (e) { return []; } }
function saveBindings(arr) { try { localStorage.setItem(LOC_BIND_KEY, JSON.stringify(arr)); } catch (e) { /* 容量/隐私模式失败忽略 */ } }
// 两个经纬度间的米距（haversine）
function distMeters(a, b) {
  const R = 6371000, toR = (x) => x * Math.PI / 180;
  const dLat = toR(b.lat - a.lat), dLng = toR(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

function AlbumTab({ data, onDayChange, currentDate }) {
  // Determine which month to show. Start from currentDate; allow navigation.
  const initRef = currentDate ? parseDate(currentDate) : new Date();
  const [yearMonth, setYearMonth] = React.useState({ year: initRef.getFullYear(), month: initRef.getMonth() + 1 });
  const [editingKey, setEditingKey] = React.useState(null); // a date key

  function shiftMonth(delta) {
    setYearMonth(({ year, month }) => {
      let m = month + delta, y = year;
      while (m < 1) { m += 12; y -= 1; }
      while (m > 12) { m -= 12; y += 1; }
      return { year: y, month: m };
    });
  }

  const { year, month } = yearMonth;
  const firstDow = (new Date(year, month - 1, 1).getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(year, month, 0).getDate();
  const totalCells = Math.ceil((firstDow + daysInMonth) / 7) * 7;
  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const dnum = i - firstDow + 1;
    if (dnum < 1 || dnum > daysInMonth) cells.push(null);
    else cells.push(new Date(year, month - 1, dnum));
  }

  function dayInfo(d) {
    const key = dateKey(d);
    return { key, day: data[key] || null };
  }

  const editingDay = editingKey ? (data[editingKey] || emptyDay(editingKey)) : null;

  // 常用地点：你以前填过的（去重，最近的在前）+ 兜底预设。点一下直接填入地址框
  const locationPresets = React.useMemo(() => {
    const used = [];
    Object.keys(data).sort().reverse().forEach(k => {
      const loc = data[k] && data[k].photoLocation && data[k].photoLocation.trim();
      if (loc && !used.includes(loc)) used.push(loc);
    });
    const merged = [...used];
    SEED_LOCATIONS.forEach(s => { if (!merged.includes(s)) merged.push(s); });
    return merged.slice(0, 10);
  }, [data]);

  // 本月心法（按日期升序）——移动端日历格子太小看不清 💡，在日历下方单独列出，一眼可见
  const monthInsights = INSIGHTS
    .filter(it => { const [y, m] = it.date.split('-').map(Number); return y === year && m === month; })
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="album">
      <div className="album-head">
        <button className="album-nav" onClick={() => shiftMonth(-1)} aria-label="上月">‹</button>
        <div className="album-title">{year} 年 {month} 月 · 时光画册</div>
        <button className="album-nav" onClick={() => shiftMonth(+1)} aria-label="下月">›</button>
      </div>
      <div className="album-grid">
        <div className="album-row album-dow">
          {['一','二','三','四','五','六','日'].map((d, i) => (
            <div key={d} className={`album-dow-cell ${i >= 5 ? 'weekend' : ''}`}>{d}</div>
          ))}
        </div>
        {Array.from({ length: totalCells / 7 }).map((_, wi) => (
          <div key={wi} className="album-row">
            {cells.slice(wi * 7, wi * 7 + 7).map((d, di) => {
              if (!d) return <div key={di} className="album-cell empty" />;
              const { key, day } = dayInfo(d);
              const hasPhoto = day && day.photo;
              const isWeekend = di >= 5;
              return (
                <button
                  key={di}
                  className={`album-cell ${isWeekend ? 'weekend' : ''} ${hasPhoto ? 'has-photo' : ''}`}
                  onClick={() => setEditingKey(key)}
                  title={key}
                >
                  <div className="album-cell-num">{d.getDate()}</div>
                  {hasPhoto ? (
                    <div className="album-photo-wrap">
                      <img src={day.photo} alt="" className="album-photo" />
                      <div className="album-caption">{day.photoCaption || (day.photoLocation && `· ${day.photoLocation}`) || ''}</div>
                    </div>
                  ) : (
                    <div className="album-empty-hint">+ 添加</div>
                  )}
                  {(INSIGHTS_BY_DATE[key] || []).length > 0 && (
                    <div
                      className="album-insights"
                      title={INSIGHTS_BY_DATE[key].join('\n')}
                    >
                      {INSIGHTS_BY_DATE[key].map((t, i) => (
                        <div key={i} className="album-insight-item">💡 {t}</div>
                      ))}
                    </div>
                  )}
                  <DayStripe day={day} />
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* 移动端专属：日历下方按时间列出本月心法（桌面端日历格够大，靠格内 💡 即可，CSS 隐藏此块） */}
      {monthInsights.length > 0 && (
        <div className="album-insight-list-m">
          <div className="album-insight-list-title">本月心法 💡（{monthInsights.length}）</div>
          {monthInsights.map((it, i) => {
            const d = parseDate(it.date);
            const wk = ['日', '一', '二', '三', '四', '五', '六'][d.getDay()];
            return (
              <button key={i} className="album-insight-list-row" onClick={() => setEditingKey(it.date)}>
                <span className="aili-date">{d.getMonth() + 1}/{d.getDate()} 周{wk}</span>
                <span className="aili-title">{it.title}</span>
              </button>
            );
          })}
        </div>
      )}

      {editingKey && editingDay && (
        <AlbumEditor
          dateKey={editingKey}
          day={editingDay}
          presets={locationPresets}
          onSave={(next) => { onDayChange(editingKey, next); setEditingKey(null); }}
          onClose={() => setEditingKey(null)}
          onClear={() => {
            onDayChange(editingKey, {
              ...editingDay,
              photo: null, photoCaption: '', photoLocation: '', photoGeo: null, photoTakenAt: null,
            });
            setEditingKey(null);
          }}
        />
      )}
    </div>
  );
}

// Thin colored stripe summarising the day's time-blocks (compact).
function DayStripe({ day }) {
  const hours = new Array(24).fill(null);
  if (day && day.tracks) {
    for (let h = 0; h < 24; h++) {
      const slots = SLOTS.map((s, idx) => ({ s, idx })).filter(o => o.s.h === h);
      const counts = {};
      slots.forEach(({ idx }) => {
        for (let t = 0; t < 3; t++) {
          const c = day.tracks[t] && day.tracks[t][idx];
          if (c) counts[c] = (counts[c] || 0) + (t === 1 ? 2 : 1);
        }
      });
      let best = null, n = 0;
      for (const k in counts) if (counts[k] > n) { best = k; n = counts[k]; }
      hours[h] = best;
    }
  }
  // 直接从 CAT_BY_ID 读颜色，自动覆盖所有内置/自定义分类
  const colorFor = (cat) => {
    if (!cat) return 'transparent';
    const c = CAT_BY_ID[cat];
    return c && c.color ? c.color : 'transparent';
  };
  return (
    <div className="album-stripe">
      {hours.map((c, h) => (
        <div key={h} className="album-stripe-cell" style={{ background: colorFor(c) }} />
      ))}
    </div>
  );
}

function AlbumEditor({ dateKey, day, onSave, onClose, onClear, presets = [] }) {
  const [photo, setPhoto] = React.useState(day.photo || null);
  const [caption, setCaption] = React.useState(day.photoCaption || '');
  const [location, setLocation] = React.useState(day.photoLocation || '');
  const [geo, setGeo] = React.useState(day.photoGeo || null);
  const [busy, setBusy] = React.useState(false);
  // 地点选项：当前已填的 + 常用预设。点开可选/可搜/可新建（如上海、北京），统一米咖色药丸
  const [locOptions, setLocOptions] = React.useState(() => {
    const names = [];
    const cur = (day.photoLocation || '').trim();
    if (cur) names.push(cur);
    presets.forEach(p => { if (!names.includes(p)) names.push(p); });
    return names.map(n => ({ v: n, label: n, color: locColor(n) }));
  });
  const [bindings, setBindings] = React.useState(loadBindings);     // 已绑定的命名地点
  const [pendingGeo, setPendingGeo] = React.useState(null);          // 点了定位、尚未绑定到名字的坐标

  function addBinding(b) {
    setBindings(prev => { const next = [...prev.filter(x => x.name !== b.name), b]; saveBindings(next); return next; });
  }
  // 点定位：拿当前坐标 → 命中已绑定地点则自动选中；否则记下坐标等你选一个名字来绑定
  function doLocate() {
    if (!navigator.geolocation) { alert('当前设备不支持定位'); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition((pos) => {
      const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setGeo(here);
      let near = null, best = LOC_BIND_RADIUS_M;
      bindings.forEach(b => { const d = distMeters(here, b); if (d <= best) { best = d; near = b; } });
      if (near) { setLocation(near.name); setPendingGeo(null); }
      else { setPendingGeo(here); }
      setBusy(false);
    }, (err) => { alert('定位失败：' + err.message); setBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 });
  }
  // 选/建地点时：若刚定过位且这个名字还没绑定，首次询问是否绑定
  function handleLocationPick(v) {
    const name = v || '';
    setLocation(name);
    if (name && pendingGeo && !bindings.some(b => b.name === name)) {
      if (window.confirm(`把刚才的定位绑定到「${name}」？以后在附近点定位会自动选中它。`)) {
        addBinding({ name, lat: pendingGeo.lat, lng: pendingGeo.lng });
      }
      setPendingGeo(null);
    }
  }
  const fileRef = React.useRef(null);

  async function pickFile(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setBusy(true);
    try {
      const dataUrl = await compressImage(f, 1200, 0.72);
      setPhoto(dataUrl);
    } catch (err) { console.error(err); }
    setBusy(false);
    e.target.value = '';
  }

  function doSave() {
    onSave({
      ...day,
      photo,
      photoCaption: caption.trim(),
      photoLocation: location.trim(),
      photoGeo: geo,
      photoTakenAt: day.photoTakenAt || new Date().toISOString(),
    });
  }

  // 有改动吗（对比打开时的原值）
  const dirty =
    photo !== (day.photo || null) ||
    caption.trim() !== (day.photoCaption || '') ||
    location.trim() !== (day.photoLocation || '');
  // 右上角 ✕：无改动直接关；有改动问是否保存（确定=保存关，取消=不保存关）
  function requestClose() {
    if (!dirty) { onClose(); return; }
    if (window.confirm('有未保存的修改，要保存吗？\n「确定」保存并关闭，「取消」不保存关闭。')) doSave();
    else onClose();
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal album-modal" onClick={e => e.stopPropagation()}>
        {/* 顶部 6px 作息彩条，像一条上边框 */}
        <div className="album-top-stripe" title="这天的作息"><DayStripe day={day} /></div>
        <button className="album-close" onClick={requestClose} aria-label="关闭" title="关闭">✕</button>
        <div className="album-head-row">
          <div className="modal-title">{dateKey} · 一日一图</div>
          <span className="album-head-sub">照片+描述+定位</span>
        </div>

        <div className="album-edit-photo">
          {photo ? (
            <div className="album-edit-photo-wrap">
              <img src={photo} alt="" className="album-edit-img" />
              <div className="album-edit-photo-actions">
                <button className="btn" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}>更换照片</button>
                <button className="btn danger" onClick={() => setPhoto(null)} disabled={busy}>移除</button>
              </div>
            </div>
          ) : (
            <div className="album-edit-empty">
              <button className="btn primary" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}>
                {busy ? '处理中…' : '+ 选择照片'}
              </button>
              <span className="album-edit-hint">支持 JPG / PNG，自动压缩到 ~1200px</span>
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={pickFile}
          />
        </div>

        <label className="album-field">
          <div className="album-field-label">描述（≤ 140 字）</div>
          <textarea
            className="album-textarea"
            rows="2"
            maxLength={140}
            value={caption}
            onChange={e => setCaption(e.target.value)}
            placeholder="一句话记下今天最难忘的事…"
          />
          <div className="album-counter">{caption.length} / 140</div>
        </label>

        <div className="album-field">
          <div className="album-field-label">定位地址<span className="album-field-sub">（点选 / 新建，或按 📍 自动就近）</span></div>
          <div className="album-loc-row">
            <div className="album-loc-select">
              <TagSelect
                multi={false}
                value={location.trim() || null}
                onChange={handleLocationPick}
                options={locOptions}
                onAddOption={(opt) => setLocOptions(prev => prev.some(o => o.v === opt.v) ? prev : [...prev, { ...opt, color: locColor(opt.v) }])}
                placeholder="选择 / 新建地点…"
              />
            </div>
            <button type="button" className="btn album-loc-gps" onClick={doLocate} disabled={busy} title="按当前定位自动选地点">📍 定位</button>
          </div>
          {pendingGeo && (
            <div className="album-loc-hint">
              已记录定位（{pendingGeo.lat.toFixed(4)}, {pendingGeo.lng.toFixed(4)}）——选一个地点即可把它绑定，下次自动认出。
            </div>
          )}
        </div>

        {(INSIGHTS_BY_DATE[dateKey] || []).length > 0 && (
          <div className="album-field">
            <div className="album-field-label">这天的心法 💡<span className="album-field-sub">（自动同步自心法沉淀，只读）</span></div>
            <div className="album-insights-list">
              {INSIGHTS_BY_DATE[dateKey].map((t, i) => (
                <div key={i} className="album-insight-row">「{t}」</div>
              ))}
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn danger" onClick={onClear}>清空</button>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn primary" onClick={doSave}>保存</button>
        </div>
      </div>
    </div>
  );
}

export { AlbumTab };
